<template>
	<div class="Page-section">
		<div class="Page-section-inner Page-section-inner--md">
			<section class="u-stack-lg">
				<form class="EditReportDefinition">
					<!-- Name -->
					<div class="EditReportDefinition-name u-stack-lg">
						<CloHeading level="2" size="3">{{ $t('tripReport.name') }} </CloHeading>
						<CloInputText
							v-model="formData.reportName"
							name="inputName"
							id="reportName"
							:label="$t('tripReport.nameLabel')"
							:helptext="$t('tripReport.nameMax', [256])"
							@input="reportNameEntered"
							:maxlength="titleCharacterLimit"
						/>
					</div>

					<!-- Dates -->
					<div class="EditReportDefinition-dates u-stack-lg">
						<CloHeading level="2" size="3">{{ $t('tripReport.dates') }}</CloHeading>
						<CloMessage v-if="sameDateMessage" role="error" title="" :actions="[]">
							{{ sameDateMessage }}
						</CloMessage>
						<CloGridAuto gap="md">
							<CloDatePicker
								enableTimeRange
								:locale="locale"
								:showMonths="2"
								dateFormat="Y-m-d"
								altFormat="j F Y"
								mode="range"
								ref="dateRangeTimePicker"
								:label="$t('tripReport.startDateLabel')"
								:endLabel="$t('tripReport.endDateLabel')"
								:startTimeLabel="$t('tripReport.startTimeLabel')"
								:endTimeLabel="$t('tripReport.endTimeLabel')"
								:defaultDateRange="[formData.startDateTime, formData.endDateTime]"
								v-on:on-change="setSelectedDateRange"
								:confirmText="$t('generic.Close')"
								:resetText="$t('generic.Reset')"
								maxDate=""
								minDate=""
								:defaultStartHour="startHours.toString()"
								:defaultStartMinute="startMinutes.toString()"
								:defaultEndHour="endHours.toString()"
								:defaultEndMinute="endMinutes.toString()"
								@on-change-end-time="setSelectedEndTime"
								@on-change-start-time="setSelectedStartTime"
							>
							</CloDatePicker>
						</CloGridAuto>
						<p class="FormHelp">{{ $t('trip_duration_too_long', [31]) }}</p>
					</div>

					<!-- Visibility -->
					<div class="EditReportDefinition-visibility u-stack-lg">
						<CloHeading level="2" size="3" class="u-stack-sm">{{ $t('tripReport.visibility') }}</CloHeading>
						<div class="u-stack-inside-sm">
							<!-- Limited -->
							<div>
								<CloInputRadio
									:label="$t('tripReport.visibilityLimited')"
									id="vis-limited"
									name="radioName"
									value="locked"
									v-model="formData.visibility"
									aria-describedby="vis-limited-desc"
									@change="setDataHasChanged"
								/>
								<p id="vis-limited-desc" class="EditReportDefinition-visibility-desc">
									{{ $t('tripReport.visibilityLimitedDesc') }}
								</p>
							</div>
							<!-- Link-only  removed v-model="checked"-->
							<div>
								<CloInputRadio
									:label="$t('tripReport.visibilityLinkOnly')"
									id="vis-linkonly"
									name="radioName"
									value="link"
									v-model="formData.visibility"
									aria-describedby="vis-linkonly-desc"
									@change="setDataHasChanged"
								/>
								<p id="vis-linkonly-desc" class="EditReportDefinition-visibility-desc">
									{{ $t('tripReport.visibilityLinkOnlyDesc') }}
								</p>
							</div>
							<!-- Public -->
							<div>
								<CloInputRadio
									:label="$t('tripReport.visibilityPublic')"
									id="vis-public"
									name="radioName"
									value="open"
									v-model="formData.visibility"
									aria-describedby="vis-public-desc"
									@change="setDataHasChanged"
								/>
								<p id="vis-public-desc" class="EditReportDefinition-visibility-desc">
									{{ $t('tripReport.visibilityPublicDesc') }}
								</p>
							</div>
						</div>
					</div>
				</form>
			</section>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloGridAuto from '@/grid/CloGridAuto/CloGridAuto.vue';
import CloInputText from '@/forms/CloInputText/CloInputText.vue';
import CloDatePicker from '@/forms/CloDatePicker/CloDatePicker.vue';
import CloInputRadio from '@/forms/CloInputRadio/CloInputRadio.vue';
import CloMessage from '@/layouts/CloMessage/CloMessage.vue';
export default {
	name: 'EditReportDefinition',
	components: {
		CloHeading,
		CloGridAuto,
		CloInputText,
		CloDatePicker,
		CloMessage,
		CloInputRadio,
	},
	data() {
		return {
			tripManageApiBaseUrl: window.tripManageApiBaseUrl,
			formData: {
				reportName: this.reportName,
				startDateTime: this.beginDt,
				endDateTime: this.endDt,
				visibility: this.privacy,
			},
			csrf: window['_csrf'],
			csrf_header: window['_csrf_header'],
			allFieldsFilled: false,
			dataHasChanged: false,
			tripDatesChanged: false,
			selectedDateRange: [],
			startHours: '0',
			startMinutes: '0',
			endHours: '23',
			endMinutes: '59',
			titleCharacterLimit: 256,
			doDatePickerReset: false,
			sameDateMessage: '',
		};
	},
	props: {
		reportName: {
			type: String,
			default: '',
		},
		beginDt: {
			type: String,
			default: '',
		},
		endDt: {
			type: String,
			default: '',
		},
		privacy: {
			type: String,
			default: 'link',
		},
		view: {
			type: String,
			default: 'definition',
		},
		mode: {
			type: String,
			default: 'create',
		},
		reportId: {
			type: Number,
			default: null,
		},
		locale: String,
	},
	mounted() {
		document.addEventListener('lichen:reset-date-range', this.clearDatePicker);

		this.setAreFieldsFilled();

		// KAGU-1742 - so am/pm change works, eventually get this into lichen
		this.$refs.dateRangeTimePicker.instanceStartTime.config.dateFormat = 'G:i K';
		this.$refs.dateRangeTimePicker.instanceEndTime.config.dateFormat = 'G:i K';
	},
	created() {
		this.initTimes();
	},
	beforeDestroy() {
		document.removeEventListener('lichen:reset-date-range', this.clearDatePicker);
	},
	methods: {
		clearDatePicker() {
			this.doDatePickerReset = true;
		},
		initTimes() {
			if (this.formData.startDateTime) {
				let startDate = new Date(this.formData.startDateTime);
				let endDate = new Date(this.formData.endDateTime);
				this.startHours = startDate.getHours();
				this.startMinutes = startDate.getMinutes();

				this.endHours = endDate.getHours();
				this.endMinutes = endDate.getMinutes();
			}
		},
		setSelectedStartTime() {
			let dateStr = this.$refs.dateRangeTimePicker.instanceStartTime.latestSelectedDateObj;
			let start = new Date(dateStr);
			this.startHours = start.getHours();
			this.startMinutes = start.getMinutes();
			this.setDataHasChanged(true);
			this.setAreFieldsFilled();
		},
		setSelectedEndTime() {
			let dateStr = this.$refs.dateRangeTimePicker.instanceEndTime.latestSelectedDateObj;
			let end = new Date(dateStr);
			this.endHours = end.getHours();
			this.endMinutes = end.getMinutes();
			this.setDataHasChanged(true);
			this.setAreFieldsFilled();
		},
		setSelectedDateRange() {
			if (this.$refs.dateRangeTimePicker.instance.selectedDates.length > 0) {
				// issue where flatpicker jumps forward a month
				// always set month to that of selected start date
				let selectedStartDate = new Date(this.$refs.dateRangeTimePicker.instance.selectedDates[0]);
				let startMonth = selectedStartDate.getMonth();
				this.$refs.dateRangeTimePicker.instance.changeMonth(startMonth, false);

				let thirtyOneDaysAhead = new Date(this.$refs.dateRangeTimePicker.instance.selectedDates[0].getTime() + 86400000 * 30);

				this.$refs.dateRangeTimePicker.instance.selectedDates[0].toUTCString();
				this.$refs.dateRangeTimePicker.instance.config.maxDate = thirtyOneDaysAhead.toUTCString();

				this.formData.startDateTime = this.$refs.dateRangeTimePicker.instance.selectedDates[0];
				this.formData.endDateTime = this.$refs.dateRangeTimePicker.instance.selectedDates[1];
			} else {
				this.$refs.dateRangeTimePicker.instance.config.maxDate = '';
				this.formData.startDateTime = null;
				this.formData.endDateTime = null;

				this.$refs.dateRangeTimePicker.instance.setDate(null);
			}
			this.doDatePickerReset = false;

			this.setDataHasChanged(true);
			this.setAreFieldsFilled();
		},
		// TODO - why can't we get the $event on this component so we can have only 1 method and detect id?

		// set hours and minutes for the start/end dates from the time picker values
		prepareDatesForSubmit() {
			let start = new Date(this.formData.startDateTime);
			start.setHours(this.startHours);
			start.setMinutes(this.startMinutes);
			let tzOffsetStart = start.getTimezoneOffset();
			let formattedDateStart = new Date(start.getTime() - tzOffsetStart * 60 * 1000);
			this.formData.startDateTime = formattedDateStart.toISOString().replace(':00.000Z', '');

			const end = new Date(this.formData.endDateTime);
			end.setHours(this.endHours);
			end.setMinutes(this.endMinutes);
			let tzOffsetEnd = end.getTimezoneOffset();
			let formattedDateEnd = new Date(end.getTime() - tzOffsetEnd * 60 * 1000);
			this.formData.endDateTime = formattedDateEnd.toISOString().replace(':00.000Z', '');
		},
		submitDefinitionForm() {
			this.prepareDatesForSubmit();
			if (this.mode === 'create' || this.dataHasChanged) {
				let baseApiUrl = '';
				let httpAction = 'post';
				if (this.mode === 'edit') {
					baseApiUrl = this.tripManageApiBaseUrl + 'edit/' + this.reportId;
					httpAction = 'patch';
				} else if (this.mode === 'create') {
					baseApiUrl = this.tripManageApiBaseUrl + 'create';
				}
				let url =
					baseApiUrl +
					'?beginDt=' +
					this.formData.startDateTime +
					'&endDt=' +
					this.formData.endDateTime +
					'&name=' +
					encodeURIComponent(this.formData.reportName) +
					'&privacy=' +
					this.formData.visibility;

				let headers = {};
				headers[this.csrf_header] = this.csrf;
				axios({
					method: httpAction,
					url: url,
					data: this.formData,
					headers: headers,
				})
					.then((res) => {
						if (this.mode === 'create') {
							let tripReportId = res.data;
							// passing welcome=true through to manage checklists, then report.
							window.location = baseUrl + 'tripreport/edit/' + tripReportId + '?view=checklists&welcome=true';
						} else if (this.mode === 'edit') {
							if (this.tripDatesChanged) {
								// send to Manage Checklists
								window.location = baseUrl + 'tripreport/edit/' + this.reportId + '?view=checklists';
							} else {
								window.location = baseUrl + 'tripreport/' + this.reportId;
							}
						}
					})
					.catch((err) => {
						console.log(err);
						if (err.response.status === 500) {
							this.sameDateMessage = this.$i18n.t('tripReport.timePeriodAlreadyExists');
						}
					});
			} else {
				window.location = baseUrl + 'tripreport/' + this.reportId;
			}
		},
		reportNameEntered() {
			this.setDataHasChanged();
			this.setAreFieldsFilled();
		},
		setAreFieldsFilled() {
			// TODO handle if someone manually clears the default time inputs.
			if (this.formData.reportName && this.formData.startDateTime && this.formData.endDateTime) {
				this.$emit('setAreFieldsFilled', true);
				return true;
			} else {
				this.$emit('setAreFieldsFilled', false);
			}
		},
		setDataHasChanged(tripDatesChanged) {
			this.dataHasChanged = true;
			if (tripDatesChanged) {
				this.tripDatesChanged = true;
			}
		},
	},
};
</script>

<style lang="scss">
.EditReportDefinition {
	.FormHelp {
		@include text(2);
		margin-top: $space-xs;
		margin-bottom: 0;
		color: $color-text-muted;
	}
}
.EditReportDefinition-dates {
	.FormHelp {
		margin-top: -($space-sm + $space-xs);
	}
}
.EditReportDefinition-visibility {
	.InputRadio {
		display: flex;
		align-items: center;
		gap: $space-sm;
		input[type='radio'] {
			margin: 0;
			width: $space-md + $space-sm;
			height: $space-md + $space-sm;
			accent-color: $color-secondary-3;
		}
		input[type='radio']:checked + label {
			font-weight: bold;
		}
		Label {
			flex-grow: 1;
		}
	}
}

.EditReportDefinition-visibility-desc {
	@include text(3);
	@include stack-sm;
	color: $color-text-muted;
	margin-left: $space-lg;
}
</style>
